import { createFileRoute, Link } from '@tanstack/react-router';
import { Accordion, Group, Stack, Text } from '@mantine/core';
import { CheckCircle2, Clock, UserRound, XCircle } from 'lucide-react';
import { Badge, Card, PageContainer, PageHeader, Pill, Section } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { useStudentDetail } from '../hooks/useGradebook';
import type { GradebookTaskDetail } from '../api/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/gradebook/$cohortId/$userId')({
  component: StudentDetail,
});

/** One student: every lab → every task → every attempt, with the grade and any instructor note. */
function StudentDetail() {
  const { cohortId, userId } = Route.useParams() as { cohortId: string; userId: string };
  const q = useStudentDetail(cohortId, userId);

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={UserRound}
        title={q.data?.student.username ?? 'Student'}
        description={q.data ? `${q.data.cohort.name} · ${q.data.labs.length} lab${q.data.labs.length === 1 ? '' : 's'} started` : ''}
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Gradebook', to: '/admin/gradebook' },
          { label: q.data?.student.username ?? '…' },
        ]}
      />
      {q.isError ? (
        <ErrorState title="Could not load the student" message={(q.error as Error)?.message ?? ''} onRetry={() => void q.refetch()} />
      ) : q.isPending || !q.data ? (
        <LoadingState rows={6} height={28} />
      ) : q.data.labs.length === 0 ? (
        <Section title="No labs started">
          <Text size="sm" c="dimmed">
            This student has not started a lab yet.
          </Text>
        </Section>
      ) : (
        <Section title="Labs" description="Expand a lab to see each task, every attempt, and the grade it earned.">
          <Accordion variant="separated" multiple defaultValue={q.data.labs.map((l) => l.slug)}>
            {q.data.labs.map((lab) => (
              <Accordion.Item key={lab.slug} value={lab.slug}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm">
                      <Text fw={600} size="sm">
                        {lab.name}
                      </Text>
                      <Badge variant={lab.status === 'completed' ? 'success' : 'info'} size="xs">
                        {lab.status}
                      </Badge>
                    </Group>
                    <Pill variant={lab.score_pct == null ? 'neutral' : lab.score_pct >= 80 ? 'success' : lab.score_pct >= 50 ? 'warn' : 'danger'} size="sm">
                      {lab.score_pct == null ? '—' : `${lab.score_pct.toFixed(0)}%`}
                    </Pill>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">
                    {lab.tasks.map((t) => (
                      <TaskRow key={t.task_index} t={t} />
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Section>
      )}
      <Link to="/admin/gradebook" search={{ cohort: cohortId }} style={{ fontSize: 13 }}>
        ← Back to gradebook
      </Link>
    </PageContainer>
  );
}

function TaskRow({ t }: { t: GradebookTaskDetail }) {
  const icon =
    t.is_correct === true ? <CheckCircle2 size={16} color="rgb(var(--c-neon))" /> :
    t.is_correct === false ? <XCircle size={16} color="rgb(var(--c-red))" /> :
    <Clock size={16} color="rgb(var(--c-amber))" />;
  return (
    <Card>
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            {icon}
            <Text size="sm" fw={500}>
              {t.task_index + 1}. {t.title}
            </Text>
            <Badge variant="info" size="xs">
              {t.check_type}
            </Badge>
          </Group>
          <Text size="sm" ff="JetBrains Mono">
            {t.points_earned}/{t.points} pts
            {t.attempts_used ? <Text span size="xs" c="dimmed"> · {t.attempts_used} attempt{t.attempts_used === 1 ? '' : 's'}</Text> : null}
          </Text>
        </Group>
        {t.attempts.length > 0 ? (
          <Stack gap={2} pl={26}>
            {t.attempts.map((a) => (
              <Group key={a.attempt_no} gap="sm" wrap="nowrap" align="flex-start">
                <Text size="xs" c="dimmed" ff="JetBrains Mono" style={{ minWidth: 20 }}>
                  #{a.attempt_no}
                </Text>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1 }}>
                  {a.answer ?? '—'}
                </Text>
                <Pill variant={a.is_correct === true ? 'success' : a.is_correct === false ? 'danger' : 'warn'} size="xs">
                  {a.is_correct === true ? `+${a.points_earned}` : a.is_correct === false ? 'miss' : 'pending'}
                </Pill>
              </Group>
            ))}
          </Stack>
        ) : null}
        {t.instructor_notes ? (
          <Text size="xs" c="dimmed" pl={26}>
            Instructor: {t.instructor_notes}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );
}
